import { useMemo } from "react";
import { createUseStyles } from "react-jss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { getEdgeIndicator, useLiveCamera } from "./viewportUtils";

/**
 * OffscreenIndicators — edge wayfinding arrows for TouchTour Studio (WP-H).
 *
 * When the active/selected scene is panned out of view, this overlay draws a
 * subtle chevron on the canvas edge nearest the node, rotated to point toward
 * it. Clicking the chevron flies the camera back to the node.
 *
 * It is a sibling of TourCanvas (not inside it): it reads the live camera from
 * the Canvas API and projects nodes with the shared viewportUtils helpers — the
 * same off-screen test EditorAlerts (WP-F) uses — so it never touches canvas
 * internals. The overlay is fixed to the canvas's bounding rect so projected
 * coordinates line up exactly with the nodes on screen.
 */

const OffscreenIndicators = ({ canvasApiRef, images, activeImage }) => {
  const classes = useStyles();
  const camera = useLiveCamera(canvasApiRef);

  const activeNode =
    activeImage != null ? (images ?? {})[activeImage] : null;

  const indicator = useMemo(
    () => (activeNode ? getEdgeIndicator(activeNode, camera) : null),
    [activeNode, camera],
  );

  const rect = camera.canvasRect;
  if (!activeNode || !indicator || !rect) return null;

  const flyTo = () => {
    const api = canvasApiRef && canvasApiRef.current;
    if (api && typeof api.jumpTo === "function") {
      api.jumpTo(activeNode.x, activeNode.y);
    }
  };

  return (
    <div
      className={classes.overlay}
      style={{
        left: rect.x,
        top: rect.y,
        width: rect.width,
        height: rect.height,
      }}
    >
      <button
        type="button"
        className={classes.chevron}
        style={{
          left: indicator.left,
          top: indicator.top,
          transform: `translate(-50%, -50%) rotate(${indicator.angle}rad)`,
        }}
        onClick={flyTo}
        title={`Fly to ${activeNode.name ?? "selected scene"}`}
      >
        <FontAwesomeIcon
          icon={["fal", "chevron-right"]}
          className={classes.icon}
        />
      </button>
    </div>
  );
};

const useStyles = createUseStyles(theme => ({
  // Fixed to the canvas's screen rect so projected node coords map 1:1. The
  // layer itself ignores pointer events; only the chevron is interactive.
  overlay: {
    position: "fixed",
    pointerEvents: "none",
    zIndex: 20,
  },
  chevron: {
    position: "absolute",
    width: 34,
    height: 34,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    margin: 0,
    pointerEvents: "auto",
    borderRadius: "50%",
    border: `1px solid ${theme.colorPrimary}`,
    backgroundColor: "rgba(20, 20, 20, 0.55)",
    color: theme.colorPrimary,
    cursor: "pointer",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.35)",
    transitionProperty: "background-color, color",
    transitionDuration: theme.transitionDuration,
    "&:hover": {
      backgroundColor: theme.colorPrimary,
      color: theme.colorWhite ?? "#fff",
    },
  },
  icon: {
    fontSize: 16,
  },
}));

export default OffscreenIndicators;
